import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import DashboardLayout from "@/components/DashboardLayout";
import { StatusBadge, formatDate, formatBytes } from "@/lib/orderUtils";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Search, Download, HardDrive, FileArchive, Trash2, User } from "lucide-react";

export default function ArchivePage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/archive/stl");
      setFiles(data);
    } catch (e) {
      toast.error("Errore nel caricamento dell'archivio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return files;
    const s = q.toLowerCase();
    return files.filter(
      (f) =>
        f.filename?.toLowerCase().includes(s) ||
        f.order_number?.toLowerCase().includes(s) ||
        f.patient_name?.toLowerCase().includes(s) ||
        f.client_name?.toLowerCase().includes(s)
    );
  }, [files, q]);

  const cases = useMemo(() => {
    const map = {};
    for (const f of filtered) {
      const k = f.order_id;
      if (!map[k]) {
        map[k] = {
          order_id: k, order_number: f.order_number, patient_name: f.patient_name,
          client_name: f.client_name, status: f.status, count: 0, size: 0, created_at: f.created_at,
        };
      }
      map[k].count += 1;
      map[k].size += f.size || 0;
      if ((f.created_at || "") > (map[k].created_at || "")) map[k].created_at = f.created_at;
    }
    return Object.values(map).sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  }, [filtered]);

  const downloadZip = async (orderId, orderNumber, patientName, clientName) => {
    setBusy(`${orderId}-zip`);
    try {
      await api.get(`/orders/${orderId}/download-zip`, { params: { check: 1 } });
      const d = new Date();
      const date = `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
      const clean = (s) => (s || "").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_");
      const fname = `${date}_${clean(patientName) || "paziente"}_${clean(clientName) || "cliente"}.zip`;
      const a = document.createElement("a");
      a.href = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/download-zip?name=${encodeURIComponent(fname)}`;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => document.body.removeChild(a), 1500);
      toast.success("Download ZIP avviato: controlla la barra download del browser");
    } catch (e) {
      const st = e.response?.status;
      toast.error(st === 404 ? "Nessun file da scaricare" : st ? `Errore nel download dello ZIP (codice ${st})` : "Errore di connessione nel download dello ZIP");
    } finally {
      setBusy(null);
    }
  };

  const deleteCase = async (orderId) => {
    setBusy(`${orderId}-del`);
    try {
      await api.delete(`/archive/stl/${orderId}`);
      toast.success("File STL del caso eliminati");
      load();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    } finally {
      setBusy(null);
    }
  };

  return (
    <DashboardLayout title="Archivio STL" subtitle="Un archivio ZIP per ogni paziente/caso, con possibilità di eliminazione.">
      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="archive-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per paziente, ordine o cliente..." className="pl-9" />
          </div>
          <Badge variant="secondary" className="ml-auto">{cases.length} casi</Badge>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
        ) : cases.length === 0 ? (
          <div className="text-center py-16">
            <HardDrive className="h-10 w-10 mx-auto text-slate-300" />
            <p className="mt-4 font-medium text-slate-700">Nessun caso in archivio</p>
            <p className="text-sm text-slate-500">I file STL caricati dai clienti compariranno qui, raggruppati per paziente.</p>
          </div>
        ) : (
          <Table data-testid="archive-table">
            <TableHeader>
              <TableRow>
                <TableHead>Paziente / Caso</TableHead>
                <TableHead>Ordine</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>File STL</TableHead>
                <TableHead>Dimensione</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Ultimo file</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cases.map((c, i) => (
                <motion.tr key={c.order_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                  data-testid="archive-row" className="border-b hover:bg-slate-50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-teal-600 shrink-0" />
                      <span className="text-sm font-medium text-slate-800">{c.patient_name || "—"}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <button onClick={() => navigate(`/orders/${c.order_id}`)} className="font-mono text-xs font-medium text-teal-600 hover:underline">
                      {c.order_number || "—"}
                    </button>
                  </TableCell>
                  <TableCell className="text-slate-600">{c.client_name || "—"}</TableCell>
                  <TableCell><Badge variant="secondary" className="font-mono">{c.count}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-500">{formatBytes(c.size)}</TableCell>
                  <TableCell>{c.status ? <StatusBadge status={c.status} /> : "—"}</TableCell>
                  <TableCell className="text-sm text-slate-500">{formatDate(c.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" data-testid="archive-zip-button" disabled={busy === `${c.order_id}-zip`} onClick={() => downloadZip(c.order_id, c.order_number, c.patient_name, c.client_name)} className="border-teal-300 text-teal-700 hover:bg-teal-50">
                        {busy === `${c.order_id}-zip` ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileArchive className="h-4 w-4" />}
                        <span className="ml-1.5 hidden sm:inline">ZIP</span>
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid="archive-delete-button" className="text-slate-400 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare i file STL di questo caso?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Verranno eliminati definitivamente i {c.count} file STL del caso di <strong>{c.patient_name || "—"}</strong> (ordine {c.order_number}). L'ordine resta in archivio ma senza i file STL. L'operazione non è reversibile.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction data-testid="archive-delete-confirm" onClick={() => deleteCase(c.order_id)} className="bg-red-600 hover:bg-red-700">Elimina file STL</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </DashboardLayout>
  );
}
